import { useEffect, useMemo, useState } from 'react';
import { buildFeedback } from '../../core/feedback';
import { ISSUE_KEY_TEXT, rankAttempts, sessionStats, summarySentence, validationReport } from '../../core/stats';
import { db, deleteSession, type AttemptRecord, type CalibrationRecord, type SessionRecord } from '../../engine/db';
import { navigate, useApp, useBlobUrl } from '../app-state';
import { BoardView } from '../components/BoardView';
import { fmtCm, fmtDate, fmtPct, fmtSec, fmtTime, Notice, OutcomeBadge, PageHead, Segmented } from '../components/bits';
import { Icon } from '../components/Icon';

export function Session({ id }: { id: string }) {
  const app = useApp();
  const [data, setData] = useState<{ session: SessionRecord; attempts: AttemptRecord[]; cal: CalibrationRecord | null } | null | undefined>(undefined);
  const [order, setOrder] = useState<'time' | 'best'>('time');

  useEffect(() => {
    (async () => {
      const session = await db.getSession(id);
      if (!session) return setData(null);
      const attempts = await db.attemptsForSession(id);
      const cal = (await db.getCalibration(session.calibrationId)) ?? null;
      setData({ session, attempts, cal });
    })();
  }, [id]);

  const stats = useMemo(() => (data ? sessionStats(data.attempts.map((a) => a.analysis), data.session.params) : null), [data]);
  const labelled = useMemo(
    () => (data ? data.attempts.filter((a) => a.label).map((a) => ({ label: a.label!, analysis: a.analysis })) : []),
    [data],
  );

  if (data === undefined) return <div className="page"><div className="spinner" /></div>;
  if (data === null) {
    return (
      <div className="page">
        <PageHead title="Session" back={() => navigate('')} />
        <p className="muted">This session no longer exists.</p>
      </div>
    );
  }
  const { session, attempts, cal } = data;
  const P = session.params;
  const ordered = order === 'best' ? rankAttempts(attempts.map((a) => a.analysis)).map((i) => attempts[i]) : attempts;
  const scatter = attempts
    .filter((a) => a.analysis.initial && (a.analysis.outcome === 'clean' || a.analysis.outcome === 'adjust'))
    .map((a) => ({ front: a.analysis.initial!.front.centre, rear: a.analysis.initial!.rear.centre, label: `Attempt ${a.index + 1}` }));
  const v = labelled.length ? validationReport(labelled) : null;

  const remove = async () => {
    if (!confirm('Delete this session, its clips and results?')) return;
    await deleteSession(session.id);
    app.toast('Session deleted');
    navigate('progress', { replace: true });
  };

  const bias = stats?.bias;
  const biasLine = (() => {
    if (!bias || scatter.length < 3) return null;
    const f = bias.front;
    if (Math.abs(f.y) >= 4 && Math.abs(f.y) >= Math.abs(f.x)) return `On average your front foot landed ${fmtCm(Math.abs(f.y))} ${f.y < 0 ? 'behind' : 'ahead of'} its target.`;
    const r = bias.rear;
    if (Math.abs(r.x) >= 4) return `On average your back foot landed ${fmtCm(Math.abs(r.x))} toward your ${r.x > 0 ? 'toes' : 'heels'}.`;
    return null;
  })();

  return (
    <div className="page stack lg">
      <PageHead title={session.mode === 'experiment' ? 'Validation set' : 'Session'} back={() => history.length > 1 ? history.back() : navigate('')} />
      <div className="stack sm">
        <p className="faint small">
          {fmtDate(session.startedAt)} · {fmtTime(session.startedAt)} · {session.source === 'demo' ? 'demo' : `${session.model} model`}
        </p>
        <h2 style={{ fontSize: 22 }}>{stats && summarySentence(stats)}</h2>
      </div>

      {stats && (
        <div className="tiles">
          <div className="tile">
            <div className="label">Clean landings</div>
            <div className="value">{fmtPct(stats.cleanRate)}</div>
            <div className="sub">of assessed attempts</div>
          </div>
          <div className="tile">
            <div className="label">Pop-up time</div>
            <div className="value">{fmtSec(stats.medianCleanPopupMs)}</div>
            <div className="sub">median, clean attempts</div>
          </div>
          <div className="tile">
            <div className="label">Front-foot scatter</div>
            <div className="value">{stats.spread ? fmtCm(stats.spread.front) : '—'}</div>
            <div className="sub">lower is more consistent</div>
          </div>
          <div className="tile">
            <div className="label">Most common issue</div>
            <div className="value" style={{ fontSize: 16, lineHeight: 1.3, marginTop: 6 }}>
              {stats.commonIssue ? ISSUE_KEY_TEXT[stats.commonIssue.key] : 'None'}
            </div>
            {stats.commonIssue && <div className="sub">{stats.commonIssue.count}× this set</div>}
          </div>
        </div>
      )}

      {cal && scatter.length > 0 && (
        <div className="card stack">
          <div>
            <h3>Where you landed</h3>
            <p className="small muted">Initial landing of every assessed attempt.</p>
          </div>
          <BoardView reference={cal.reference} stance={cal.stance} tol={{ along: P.tolAlong, across: P.tolAcross }} tape={cal.tape} scatter={scatter} />
          {biasLine && <Notice>{biasLine}</Notice>}
        </div>
      )}

      {v && (
        <div className="card stack sm">
          <h3>Against your labels</h3>
          <p className="small muted">
            {v.labelled} labelled · outcome agreement {fmtPct(v.agreement)} · assessed {fmtPct(v.coverage)} of real attempts
            {v.correctedScoredClean > 0 && ` · ${v.correctedScoredClean} shuffled landing(s) scored clean`}
          </p>
          <a className="small" href="#/lab">Open the validation lab</a>
        </div>
      )}

      <div>
        <div className="row between">
          <div className="section-title">Attempts</div>
          <Segmented label="Order" value={order} onChange={setOrder} options={[{ value: 'time', label: 'In order' }, { value: 'best', label: 'Best first' }]} />
        </div>
        <div className="card flush list" style={{ marginTop: 8 }}>
          {ordered.map((a) => (
            <AttemptRow key={a.id} a={a} line={a.analysis.outcome === 'clean' ? 'Both feet on target, held still.' : buildFeedback(a.analysis, P).spoken} />
          ))}
        </div>
      </div>

      <button className="btn danger" onClick={remove}>
        <Icon name="trash" size={18} /> Delete session
      </button>
    </div>
  );
}

function AttemptRow({ a, line }: { a: AttemptRecord; line: string }) {
  const thumb = useBlobUrl(a.stills.settled?.key ?? a.stills.landing?.key);
  return (
    <a className="attempt-row" href={`#/attempt/${a.id}`}>
      <span className="idx">{a.index + 1}</span>
      {thumb ? <img className="thumb" src={thumb} alt="" /> : <span className="thumb" />}
      <div className="grow stack sm" style={{ gap: 4 }}>
        <OutcomeBadge outcome={a.analysis.outcome} />
        <span className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{line}</span>
      </div>
      <span className="small faint num">{a.analysis.timing.popupMs !== null ? fmtSec(a.analysis.timing.popupMs) : ''}</span>
      <Icon name="chevron" size={18} className="faint" />
    </a>
  );
}
